import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Modal,
  Box,
  Stack,
  Typography,
  IconButton,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  Divider,
  ButtonQuantityControl,
  Button,
  Tag,
  AlertBanner,
  makeMessage,
  makeTitle,
  renderInput,
  Header,
  ListItem,
  getIcon,
} from "@dishopsaas/design-system";
import {
  modalSx,
  scrollAreaSx,
  closeButtonSx,
  stickyHeaderSx,
  footerSx,
  imageSx,
  IMAGE_HEIGHT,
} from "./ProductDetailModal.styles";
import {
  initializeNewProduct,
  ensureProductOfferCategory,
  getMinimumStockRemaining,
  getOptionProps,
  getMissingRequiredCategory,
  scrollToCategory,
  computeStickyHeader,
  isUniqueOption,
  generateOptionChoiceMessage,
  isCheckedOrSelected,
  handleCounterClickService,
  handleRadioButtonPress,
  handleCheckBoxPressService,
  generateCounterOptionKey,
  getSortedCategoryItems,
  getSelectedCount,
  getStockRemainingMessage,
} from "./ProductDetailModal.services";
import { getTotalPrice, updateOrderService } from "../../../../../utils";
import { getProductTempPrice } from "../../../../../utils/offer";
import { CATEGORY_SEPARATOR, ICONS } from "../../../../../constants";
import { isObjectDefined } from "../../../../../utils/object";
import { clampLines, isStringNotNull } from "../../../../../utils";
import { normalizeStorageImageUrl } from "../../../../../utils/imageUrl";
import {
  getBngmPreviewTotal,
  isBngmOffer,
} from "../../../../../utils/bngmOffer";
import _ from "lodash";

const createInitialState = () => ({
  selectedOptions: {},
  sortedCategories: [],
  productItems: [],
  nbProduct: 0,
  linkBetweenOptionsAndSubOptions: new Map(),
  itemsCounter: {},
  instruction: "",
  missingCategoryId: null,
  isLoading: false,
  showStickyHeader: false,
  categories: {},
});

export default function ProductDetailModalView(props) {
  const {
    disableProductInstruction,
    price,
    minimumPrice,
    productOffer,
    sectionKey,
    shopCategories,
    pendingOrder,
    showModal,
    product,
    onClose,
    setShowModal,
    sectionName,
    promoText,
    disabledMessage,
    offerProductsNbInPendingOrderBasket,
    potentialLimit,
  } = props;
  const theme = useTheme();
  const [state, setStateRaw] = useState(createInitialState);
  const setState = useCallback(
    (update) => setStateRaw((prev) => ({ ...prev, ...update })),
    [],
  );
  const {
    showStickyHeader,
    isLoading,
    missingCategoryId,
    itemsCounter,
    sortedCategories,
    nbProduct,
    productItems,
    instruction,
  } = state;
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const rafId = useRef<number | null>(null);
  const stickyThresholdRef = useRef(0);

  const { stockRemaining } = useMemo(
    () =>
      getMinimumStockRemaining(
        productItems,
        product,
        pendingOrder,
        nbProduct,
      ) as { stockRemaining: number },
    [productItems, nbProduct, product, pendingOrder],
  );
  const isLimitReached =
    stockRemaining <= 0 ||
    (isObjectDefined(potentialLimit) &&
      Number(potentialLimit) <=
        offerProductsNbInPendingOrderBasket + nbProduct);

  useEffect(() => {
    if (!showModal) {
      setState(createInitialState());
      stickyThresholdRef.current = 0;
      return;
    }

    const categoriesToAdd =
      product?.categories?.split(CATEGORY_SEPARATOR) || [];
    initializeNewProduct(
      shopCategories,
      categoriesToAdd,
      state,
      setState,
      pendingOrder,
      stockRemaining,
      productOffer,
      product,
      sectionKey,
    );
  }, [showModal]);

  useEffect(() => {
    const ensured = ensureProductOfferCategory({
      productOffer,
      product,
      sectionKey,
      categoriesProps: shopCategories,
      sortedCategories,
    });

    if (ensured && ensured !== sortedCategories) {
      setState({ sortedCategories: ensured });
    }
  }, [productOffer, product, sectionKey, shopCategories, sortedCategories]);

  const handleCounterClick = useCallback(
    (item, category, key, isAdd, increaseDisabled) => {
      handleCounterClickService({
        increaseDisabled,
        item,
        category,
        key,
        isAdd,
        itemsCounter,
        productItems,
        state,
        setState,
      });
    },
    [itemsCounter, productItems, state],
  );
  const onPressRadioButton = useCallback(
    (index, category, item, isSelected) =>
      handleRadioButtonPress({
        index,
        category,
        item,
        isSelected,
        state,
        productItems,
        itemsCounter,
        setState,
      }),
    [state, productItems, itemsCounter],
  );
  const onPressCheckBox = useCallback(
    (key, category, item, checked) => {
      handleCheckBoxPressService({
        key,
        category,
        item,
        checked,
        productItems,
        state,
        setState,
      });
    },
    [productItems, state],
  );

  const updateProduct = useCallback(
    (nextQuantity: number) => {
      if (nextQuantity > nbProduct + stockRemaining) return;
      setState({ nbProduct: Math.max(nextQuantity, 0) });
    },
    [nbProduct, stockRemaining],
  );

  const totalPrice = useMemo(
    () => getTotalPrice(nbProduct, product.price, sectionKey, productItems),
    [nbProduct, product.price, sectionKey, productItems],
  );

  const displayedTotalPrice = useMemo(
    () =>
      isBngmOffer(productOffer)
        ? getBngmPreviewTotal(
            product.price,
            productItems,
            productOffer,
            nbProduct,
            sectionKey,
          )
        : productOffer
          ? getProductTempPrice(
              { ...product, nb: nbProduct, totalPrice },
              productOffer,
            )
          : totalPrice,
    [product, price, minimumPrice, productOffer, totalPrice],
  );

  const updateOrder = useCallback(async () => {
    await updateOrderService({
      setShowModal,
      product,
      productItems,
      instruction,
      nbProduct,
      sectionName,
      sectionKey,
      pendingOrder,
      productOffer,
    });
  }, [
    instruction,
    nbProduct,
    productItems,
    pendingOrder,
    productOffer,
    product,
    sectionKey,
    sectionName,
    setShowModal,
  ]);

  const renderStickyHeader = () => {
    if (!showStickyHeader) return null;
    return (
      <Box sx={stickyHeaderSx}>
        {product.image && (
          <Avatar
            src={product.image}
            alt={product.name}
            sx={{ width: 30, height: 30 }}
          />
        )}
        <Typography variant="h5">{product.name}</Typography>
      </Box>
    );
  };
  const renderFooter = () => (
    <Box sx={footerSx}>
      <ButtonQuantityControl
        quantity={nbProduct}
        onIncrease={() => handleQuantityChange(1)}
        onDecrease={() => handleQuantityChange(-1)}
        increaseDisabled={isLimitReached}
      />

      <Button
        label={"Ajouter au panier"}
        onClick={handleAddToCart}
        loading={isLoading}
        disabled={isLoading}
        width={"100%"}
        discount={discount}
        price={textPrice}
      />
    </Box>
  );

  const handleQuantityChange = useCallback(
    (delta: number) => {
      if (!updateProduct) return;
      const newQuantity = Math.max(1, nbProduct + delta);
      updateProduct(newQuantity);
    },
    [nbProduct, updateProduct],
  );

  const handleAddToCart = useCallback(async () => {
    const missingId = getMissingRequiredCategory(
      sortedCategories,
      productItems,
      itemsCounter,
    );

    if (missingId) {
      setState({ missingCategoryId: missingId });
      scrollToCategory(missingId, categoryRefs, scrollContainerRef);
      return;
    }

    setState({ missingCategoryId: null, isLoading: true });

    try {
      await updateOrder();
    } finally {
      setState({ isLoading: false });
    }
  }, [sortedCategories, instruction, productItems, updateOrder]);

  const handleScroll = useCallback(() => {
    if (rafId.current != null) return;

    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const container = scrollContainerRef.current;
      if (!container) return;

      const scroll = container.scrollTop;

      if (imageRef.current && product.image) {
        const opacity = Math.max(0, 1 - scroll / IMAGE_HEIGHT);
        imageRef.current.style.opacity = String(opacity);
      }

      setStateRaw((prev) => {
        const { shouldShow, threshold } = computeStickyHeader(
          container,
          titleRef.current,
          prev.missingCategoryId,
          categoryRefs.current,
          prev.showStickyHeader,
          stickyThresholdRef.current,
          !!product.image,
        );
        stickyThresholdRef.current = threshold;
        return prev.showStickyHeader === shouldShow
          ? prev
          : { ...prev, showStickyHeader: shouldShow };
      });
    });
  }, [product.image]);

  useEffect(() => {
    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
    };
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleResize = () => handleScroll();
    window.addEventListener("resize", handleResize);
    handleScroll();
    return () => window.removeEventListener("resize", handleResize);
  }, [handleScroll, sortedCategories]);

  const categoryNumbers = useMemo(() => {
    const numbers = {};
    let counter = 0;
    sortedCategories.forEach((category) => {
      if (!category) return;
      counter += 1;
      numbers[category.key] = counter;
    });
    return numbers;
  }, [sortedCategories]);

  const textPrice =
    !product.isLoyaltyProduct && !product.productPromotionCode
      ? String(displayedTotalPrice)
      : null;
  const discount = displayedTotalPrice < totalPrice ? String(totalPrice) : null;

  const renderCategories = (missingCategoryId?: string) => {
    if (!showModal) return null;
    return (
      <Stack spacing={2.5} pb={2.5}>
        {sortedCategories.map((category, index) => {
          if (!category) return null;

          const isMultiple = !!category.multiple && category.max !== 1;
          const uniqueOption = isUniqueOption(category);
          const items = getSortedCategoryItems(category, productItems, index);
          const selectedCount = getSelectedCount(
            productItems,
            category,
            itemsCounter,
            index,
          );

          const min = category?.min ?? (uniqueOption ? 1 : 0);
          const status: "obligatoire" | "facultatif" | "selectionne" =
            selectedCount > 0 && selectedCount >= min
              ? "selectionne"
              : min > 0
                ? "obligatoire"
                : "facultatif";

          return (
            <Box
              key={category.key}
              mt={3}
              ref={(el: any) => {
                categoryRefs.current[category.key] = el;
              }}
            >
              <Header
                containerStyle={{ px: 4, mb: 3, gap: 0.2 }}
                alignItems="flex-start"
                title={category.name}
                description={generateOptionChoiceMessage(category)}
                status={status}
                counterValue={categoryNumbers[category.key]}
                withStatus
                isError={
                  missingCategoryId === category.key && selectedCount < min
                }
              />

              <Stack spacing={1}>
                {items.map((item) => {
                  const commonProps = getOptionProps({
                    item,
                    index,
                    productItems,
                    pendingOrder,
                    nbProduct,
                    category,
                    missingCategoryId,
                    selectedCount,
                    min,
                    isMultiple,
                    uniqueOption,
                  });

                  if (isMultiple) {
                    const optionKey = generateCounterOptionKey(
                      category.key,
                      item.key,
                    );
                    const counter = itemsCounter[optionKey] ?? 0;
                    return (
                      <ListItem
                        {...commonProps}
                        quantity={counter}
                        onIncrease={() =>
                          handleCounterClick(
                            item,
                            category,
                            index,
                            true,
                            commonProps.increaseDisabled,
                          )
                        }
                        onDecrease={() =>
                          handleCounterClick(
                            item,
                            category,
                            index,
                            false,
                            commonProps.increaseDisabled,
                          )
                        }
                      />
                    );
                  }

                  const isSelected = isCheckedOrSelected(
                    productItems,
                    index,
                    item,
                  );

                  return (
                    <ListItem
                      {...commonProps}
                      type={uniqueOption ? "radio" : "checkbox"}
                      checked={isSelected}
                      isCustomColor
                      disabled={
                        uniqueOption
                          ? commonProps.uniqueOptionDisabled
                          : commonProps.checkBoxDisabled
                      }
                      onChange={() => {
                        const handler = uniqueOption
                          ? onPressRadioButton
                          : onPressCheckBox;
                        handler(index, category, item, isSelected);
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    onClose(event, reason);
  };

  const stockRemainingMessage = getStockRemainingMessage(
    stockRemaining,
    product.name,
  );
  const displayImage = normalizeStorageImageUrl(
    isStringNotNull(product?.imageCompressed)
      ? product.imageCompressed
      : product.image,
  );
  return (
    <Modal open={showModal} onClose={handleClose}>
      <Box sx={modalSx}>
        {renderStickyHeader()}
        <IconButton
          onClick={onClose}
          sx={closeButtonSx(theme, showStickyHeader)}
        >
          {getIcon(ICONS.Close, { size: 14 })}
        </IconButton>
        <Box ref={scrollContainerRef} sx={scrollAreaSx} onScroll={handleScroll}>
          {product.image && (
            <Box ref={imageRef} sx={imageSx(theme, product.image)} />
          )}
          <Box sx={{ p: 4, ...(product.image ? {} : { mt: 5 }) }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography sx={{ ...clampLines() }} ref={titleRef} variant="h5">
                {product.name}
              </Typography>

              {promoText?.title && (
                <Tag
                  label={promoText?.title}
                  color="successLight"
                  borderRadius={4}
                />
              )}
            </Stack>

            {product.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...clampLines(), display: "block", mb: 2 }}
              >
                {product.description}
              </Typography>
            )}

            {promoText?.title && (
              <AlertBanner
                title={makeTitle(promoText?.title)}
                severity={"warning"}
                iconName="Gift"
                noShadow={true}
                message={
                  promoText?.description && makeMessage(promoText?.description)
                }
              />
            )}
          </Box>
          {!_.isEmpty(sortedCategories) && renderCategories(missingCategoryId)}
          {!disableProductInstruction && (
            <Box p={4}>
              <Divider sx={{ mb: 4 }} />
              {renderInput({
                label: "Instructions spéciales",
                placeholder: "Entrez vos instructions",
                value: instruction,
                onChange: (e) => setState({ instruction: e.target.value }),
                type: "text",
                multiline: true,
                minRows: 1,
                maxRows: 6,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    pt: 0,
                    px: 0,
                    borderWidth: 1.5,
                  },
                  "& .MuiInputBase-input": {
                    paddingTop: 16,
                    paddingLeft: 16,
                    paddingRight: 16,
                    color: "transparent",
                    pointerEvents: "none",
                  },
                },
              })}
            </Box>
          )}
          {disabledMessage && (
            <Box px={4} pb={2}>
              <AlertBanner
                severity={disabledMessage.severity}
                message={makeMessage(disabledMessage.text)}
                noShadow
              />
            </Box>
          )}
        </Box>
        {stockRemainingMessage && (
          <Box px={4} pb={2}>
            <AlertBanner
              severity={stockRemainingMessage.severity}
              message={makeMessage(stockRemainingMessage.text)}
              noShadow
            />
          </Box>
        )}
        {renderFooter()}
      </Box>
    </Modal>
  );
}
